import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Wallet, Upload, Loader2, FileSpreadsheet, Sparkles, CheckCircle2, XCircle,
  Search, Filter, Calendar, Users, Building2, ReceiptText, Eye, Pencil,
  HandCoins, BadgeIndianRupee,
} from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import PageHeader from '@/components/PageHeader';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';

const REACT_APP_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_MB = 8;
const CATEGORY_STYLE = {
  'Vendor Payment':  { badge: 'border-sky-500/40 text-sky-200 bg-sky-500/10',    icon: Building2 },
  'Employee Salary': { badge: 'border-emerald-500/40 text-emerald-200 bg-emerald-500/10', icon: Users },
  'Advance':         { badge: 'border-amber-500/40 text-amber-200 bg-amber-500/10',       icon: Wallet },
  'Expense':         { badge: 'border-rose-500/40 text-rose-200 bg-rose-500/10',           icon: ReceiptText },
  'Other':           { badge: 'border-slate-500/40 text-slate-200 bg-slate-500/10',        icon: FileSpreadsheet },
};
const STATUS_STYLE = {
  'Pending Review': 'border-amber-500/40 text-amber-200 bg-amber-500/10',
  'Approved':       'border-emerald-500/40 text-emerald-200 bg-emerald-500/10',
  'Rejected':       'border-rose-500/40 text-rose-200 bg-rose-500/10',
};

const fmtINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => (d ? new Date(`${String(d).slice(0, 10)}T00:00:00`).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '—');

async function fileToScreenshot(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const raw = reader.result || '';
      const b64 = String(raw).split(',')[1] || '';
      resolve({ filename: file.name, mime_type: file.type || 'image/png', data_base64: b64 });
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// --------------- Submit form ---------------
function SubmitForm({ meta, onCreated }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [note, setNote] = useState('');
  const [reimbursementNeeded, setReimbursementNeeded] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [extracted, setExtracted] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  // Final confirmed form values (pre-filled from extraction, editable by submitter)
  const [form, setForm] = useState({
    amount: '', payee: '', date: '', category: 'Expense',
    reference_number: '', employee_id: '', employee_name: '', vendor_name: '',
    linked_client_id: '', linked_client_name: '',
  });

  const employeeOptions = meta?.employees || [];

  const reset = () => {
    setFile(null); setPreview(null); setNote(''); setExtracted(null); setReimbursementNeeded(false);
    setForm({ amount: '', payee: '', date: '', category: 'Expense',
      reference_number: '', employee_id: '', employee_name: '', vendor_name: '',
      linked_client_id: '', linked_client_name: '' });
  };

  const pickFile = async (f) => {
    if (!f) return;
    if (!/^image\/(png|jpeg|jpg|webp)$/i.test(f.type)) {
      toast.error('Only PNG, JPEG, or WEBP screenshots supported');
      return;
    }
    if (f.size > MAX_MB * 1024 * 1024) {
      toast.error(`Image must be under ${MAX_MB} MB`);
      return;
    }
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setExtracted(null);
  };

  const runExtract = async () => {
    if (!file) { toast.error('Upload a screenshot first'); return; }
    setExtracting(true);
    try {
      const screenshot = await fileToScreenshot(file);
      const r = await api.post('/finance/transactions/extract', { screenshot, note });
      const ex = r.data?.extracted || {};
      setExtracted(ex);
      // Pre-fill the confirm form with the AI values — human can still edit.
      setForm((f) => ({
        ...f,
        amount: ex.extracted_amount ?? '',
        payee: ex.extracted_payee ?? '',
        date: ex.extracted_date ?? '',
        category: ex.extracted_category_guess && ['Vendor Payment', 'Employee Salary', 'Advance', 'Expense', 'Other'].includes(ex.extracted_category_guess)
          ? ex.extracted_category_guess : (f.category || 'Expense'),
        reference_number: ex.extracted_reference ?? f.reference_number,
      }));
      toast.success('AI extraction complete — review the values below');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Extraction failed — please fill values manually');
    } finally {
      setExtracting(false);
    }
  };

  const submit = async () => {
    if (!file) { toast.error('Upload a screenshot first'); return; }
    if (!form.amount || Number(form.amount) <= 0) { toast.error('Amount required'); return; }
    if (!form.payee?.trim()) { toast.error('Payee required'); return; }
    if (!form.date) { toast.error('Date required'); return; }
    setSubmitting(true);
    try {
      const screenshot = await fileToScreenshot(file);
      const emp = employeeOptions.find((e) => e.id === form.employee_id);
      const payload = {
        screenshot,
        amount: Number(form.amount),
        payee: form.payee.trim(),
        date: form.date,
        category: form.category,
        reference_number: form.reference_number || null,
        employee_id: form.category === 'Employee Salary' || form.category === 'Advance' ? (form.employee_id || null) : null,
        employee_name: form.category === 'Employee Salary' || form.category === 'Advance' ? (emp?.name || form.employee_name || null) : null,
        vendor_name: form.category === 'Vendor Payment' ? (form.vendor_name || form.payee || null) : null,
        linked_client_id: form.linked_client_id || null,
        linked_client_name: form.linked_client_name || null,
        note,
        reimbursement_needed: !!reimbursementNeeded,
        extracted_amount: extracted?.extracted_amount ?? null,
        extracted_payee: extracted?.extracted_payee ?? null,
        extracted_date: extracted?.extracted_date ?? null,
        extracted_reference: extracted?.extracted_reference ?? null,
        extracted_category_guess: extracted?.extracted_category_guess ?? null,
        extracted_raw_notes: extracted?.extracted_raw_notes ?? null,
      };
      const r = await api.post('/finance/transactions', payload);
      toast.success('Transaction submitted for review');
      onCreated && onCreated(r.data);
      reset();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="shine-card p-5 rounded-xl">
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Left — upload + preview */}
        <div className="space-y-3">
          <Label className="text-white font-semibold">1. Upload payment screenshot</Label>
          <label
            className="flex items-center justify-center rounded-xl border-2 border-dashed border-amber-500/30 hover:border-amber-500/50 bg-[#0f2240]/50 min-h-[220px] cursor-pointer text-center px-4 py-6 transition"
            data-testid="finance-upload-drop"
          >
            <input
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp"
              className="hidden"
              onChange={(e) => pickFile(e.target.files?.[0])}
              data-testid="finance-upload-input"
            />
            {preview ? (
              <img src={preview} alt="Screenshot preview" className="max-h-[400px] rounded-lg" data-testid="finance-preview" />
            ) : (
              <div className="flex flex-col items-center gap-2 text-slate-300">
                <Upload className="h-8 w-8 text-amber-300" />
                <div className="font-semibold text-white">Click to upload a payment screenshot</div>
                <div className="text-xs text-slate-400">PNG, JPEG or WEBP · up to {MAX_MB} MB</div>
              </div>
            )}
          </label>
          {file && (
            <div className="flex items-center justify-between gap-2 text-xs text-slate-400">
              <span className="truncate">{file.name} · {(file.size / 1024).toFixed(0)} KB</span>
              <button onClick={reset} className="text-rose-300 hover:text-rose-200">Clear</button>
            </div>
          )}
          <div>
            <Label>Note <span className="text-xs text-slate-500">— what is this payment for?</span></Label>
            <Textarea
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Freelance video editor payment for July"
              className="bg-[#0f2240] border-amber-500/20 text-white"
              data-testid="finance-note-input"
            />
          </div>
          <Button
            onClick={runExtract}
            disabled={!file || extracting}
            data-testid="finance-extract-btn"
            className="w-full bg-amber-500 hover:bg-amber-400 text-[#0a1628] font-semibold"
          >
            {extracting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Reading screenshot…</> : <><Sparkles className="h-4 w-4 mr-2" /> Auto-fill from screenshot</>}
          </Button>
        </div>

        {/* Right — confirm form */}
        <div className="space-y-3">
          <Label className="text-white font-semibold">2. Confirm the details</Label>
          <p className="text-xs text-slate-400 -mt-1">
            The AI fills these in for you — always double-check before submitting.
            Nothing is saved until you click <span className="text-amber-300 font-semibold">Submit for review</span>.
          </p>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Amount (₹)</Label>
              <Input type="number" min="0" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })}
                className="bg-[#0f2240] border-amber-500/20 text-white" data-testid="finance-amount-input" />
            </div>
            <div>
              <Label>Date</Label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
                className="bg-[#0f2240] border-amber-500/20 text-white" data-testid="finance-date-input" />
            </div>
          </div>

          <div>
            <Label>Payee / recipient</Label>
            <Input value={form.payee} onChange={(e) => setForm({ ...form, payee: e.target.value })}
              placeholder="Name or UPI ID visible in the screenshot"
              className="bg-[#0f2240] border-amber-500/20 text-white" data-testid="finance-payee-input" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="bg-[#0f2240] border-amber-500/20 text-white" data-testid="finance-category-trigger"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0a1628] border-amber-500/20">
                  {(meta?.categories || []).map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Reference # <span className="text-xs text-slate-500">(optional)</span></Label>
              <Input value={form.reference_number} onChange={(e) => setForm({ ...form, reference_number: e.target.value })}
                placeholder="UPI ref / UTR"
                className="bg-[#0f2240] border-amber-500/20 text-white" />
            </div>
          </div>

          {(form.category === 'Employee Salary' || form.category === 'Advance') && (
            <div>
              <Label>Employee</Label>
              <Select value={form.employee_id || '_none'} onValueChange={(v) => setForm({ ...form, employee_id: v === '_none' ? '' : v })}>
                <SelectTrigger className="bg-[#0f2240] border-amber-500/20 text-white" data-testid="finance-employee-trigger"><SelectValue placeholder="Pick an employee" /></SelectTrigger>
                <SelectContent className="bg-[#0a1628] border-amber-500/20">
                  <SelectItem value="_none">— None —</SelectItem>
                  {employeeOptions.map((e) => (
                    <SelectItem key={e.id} value={e.id}>{e.name} <span className="text-slate-500 text-xs">· {e.role}</span></SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {form.category === 'Vendor Payment' && (
            <div>
              <Label>Vendor name <span className="text-xs text-slate-500">(defaults to payee)</span></Label>
              <Input value={form.vendor_name || form.payee} onChange={(e) => setForm({ ...form, vendor_name: e.target.value })}
                className="bg-[#0f2240] border-amber-500/20 text-white" />
            </div>
          )}

          {extracted && (
            <Card className="bg-[#0f2240]/70 border border-amber-500/15 p-3 rounded-lg" data-testid="finance-ai-audit">
              <div className="text-[10px] uppercase tracking-wider text-amber-300 font-bold flex items-center gap-1 mb-1"><Sparkles className="h-3 w-3" /> What the AI read</div>
              <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-slate-300">
                <div>Amount: <span className="text-white">{extracted.extracted_amount != null ? fmtINR(extracted.extracted_amount) : '—'}</span></div>
                <div>Date: <span className="text-white">{extracted.extracted_date || '—'}</span></div>
                <div>Payee: <span className="text-white truncate">{extracted.extracted_payee || '—'}</span></div>
                <div>Ref: <span className="text-white truncate">{extracted.extracted_reference || '—'}</span></div>
                <div className="col-span-2">Guess: <span className="text-white">{extracted.extracted_category_guess || '—'}</span></div>
                {extracted.extracted_raw_notes && <div className="col-span-2 text-slate-400 italic">“{extracted.extracted_raw_notes}”</div>}
              </div>
            </Card>
          )}

          <label
            className="flex items-start gap-2 rounded-lg border border-amber-500/20 bg-amber-500/5 px-3 py-2 cursor-pointer hover:border-amber-500/40 transition"
            data-testid="finance-reimbursement-toggle"
          >
            <input
              type="checkbox"
              checked={reimbursementNeeded}
              onChange={(e) => setReimbursementNeeded(e.target.checked)}
              className="mt-1 accent-amber-500"
            />
            <div className="text-xs">
              <div className="text-white font-semibold flex items-center gap-1.5">
                <HandCoins className="h-3.5 w-3.5 text-amber-300" /> I paid this from my own pocket — reimburse me
              </div>
              <div className="text-slate-400 mt-0.5">Owner/Admin will see this in the Reimbursements queue after it's approved and can mark it as paid.</div>
            </div>
          </label>

          <Button
            onClick={submit}
            disabled={submitting || !file}
            data-testid="finance-submit-btn"
            className="w-full bg-emerald-500 hover:bg-emerald-400 text-[#0a1628] font-semibold"
          >
            {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Submitting…</> : 'Submit for review'}
          </Button>
        </div>
      </div>
    </Card>
  );
}

// --------------- Detail dialog ---------------
function TransactionDetail({ txnId, onClose, onChanged, currentUserId, canReview, canManage }) {
  const [txn, setTxn] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectNote, setRejectNote] = useState('');
  const [acting, setActing] = useState(false);

  useEffect(() => {
    if (!txnId) return;
    setLoading(true);
    api.get(`/finance/transactions/${txnId}`).then((r) => setTxn(r.data)).finally(() => setLoading(false));
  }, [txnId]);

  const approve = async () => {
    setActing(true);
    try {
      const r = await api.post(`/finance/transactions/${txnId}/approve`);
      setTxn(r.data);
      toast.success('Approved');
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Approve failed');
    } finally { setActing(false); }
  };
  const reject = async () => {
    if (!rejectNote.trim()) { toast.error('Please give a reason'); return; }
    setActing(true);
    try {
      const r = await api.post(`/finance/transactions/${txnId}/reject`, { review_note: rejectNote.trim() });
      setTxn(r.data);
      toast.success('Rejected — submitter notified');
      setRejectOpen(false); setRejectNote('');
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reject failed');
    } finally { setActing(false); }
  };

  const markReimbursed = async () => {
    setActing(true);
    try {
      const r = await api.post(`/finance/transactions/${txnId}/mark-reimbursed`);
      setTxn(r.data);
      toast.success('Marked as reimbursed — submitter notified');
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not mark as paid');
    } finally { setActing(false); }
  };

  const isSubmitter = txn && txn.submitted_by === currentUserId;
  const isPending = txn && txn.status === 'Pending Review';
  const canAct = canReview && isPending && !isSubmitter;

  return (
    <Dialog open={!!txnId} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#0a1628] border-amber-500/20 text-slate-100 max-w-4xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <ReceiptText className="h-5 w-5 text-amber-300" /> Transaction detail
          </DialogTitle>
        </DialogHeader>
        {loading || !txn ? (
          <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-amber-300" /></div>
        ) : (
          <div className="grid lg:grid-cols-2 gap-5 py-2">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Original screenshot</div>
              <img
                src={`data:${txn.screenshot?.mime_type};base64,${txn.screenshot?.data_base64}`}
                alt="Payment screenshot"
                className="w-full rounded-lg border border-amber-500/15"
                data-testid="detail-screenshot"
              />
            </div>
            <div className="space-y-3">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="outline" className={STATUS_STYLE[txn.status] || ''}>{txn.status}</Badge>
                <Badge variant="outline" className={CATEGORY_STYLE[txn.category]?.badge || ''}>{txn.category}</Badge>
                {txn.reimbursement_needed && (
                  <Badge variant="outline" className={txn.reimbursement_status === 'Paid'
                    ? 'border-emerald-500/40 text-emerald-200 bg-emerald-500/10'
                    : 'border-amber-500/40 text-amber-200 bg-amber-500/10'}>
                    <HandCoins className="h-3 w-3 mr-1" /> Reimburse · {txn.reimbursement_status || 'Pending'}
                  </Badge>
                )}
                <span className="text-xs text-slate-500">Submitted by {txn.submitted_by_name}</span>
              </div>
              <div className="text-3xl font-extrabold text-white">{fmtINR(txn.amount)}</div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Payee</div><div className="text-white font-semibold truncate">{txn.payee}</div></div>
                <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Date</div><div className="text-white">{fmtDate(txn.date)}</div></div>
                {txn.reference_number && <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Reference</div><div className="text-white break-all">{txn.reference_number}</div></div>}
                {txn.employee_name && <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Employee</div><div className="text-white">{txn.employee_name}</div></div>}
                {txn.vendor_name && <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Vendor</div><div className="text-white">{txn.vendor_name}</div></div>}
                {txn.linked_client_name && <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Linked client</div><div className="text-white">{txn.linked_client_name}</div></div>}
              </div>
              {txn.note && (
                <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Submitter's note</div><div className="text-sm text-slate-200 italic">“{txn.note}”</div></div>
              )}
              {txn.review_note && (
                <div className="rounded-md bg-rose-500/10 border border-rose-500/25 p-2">
                  <div className="text-[10px] uppercase tracking-wider text-rose-300">Reviewer's note</div>
                  <div className="text-sm text-rose-100">{txn.review_note}</div>
                  <div className="text-[10px] text-slate-400 mt-1">by {txn.reviewed_by_name} · {fmtDate(txn.reviewed_at?.slice(0, 10))}</div>
                </div>
              )}
              {(txn.extracted_amount != null || txn.extracted_payee || txn.extracted_raw_notes) && (
                <Card className="bg-[#0f2240]/70 border border-amber-500/15 p-3 rounded-lg">
                  <div className="text-[10px] uppercase tracking-wider text-amber-300 font-bold flex items-center gap-1 mb-1"><Sparkles className="h-3 w-3" /> AI extraction (for audit)</div>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-slate-300">
                    <div>Amount: <span className="text-white">{txn.extracted_amount != null ? fmtINR(txn.extracted_amount) : '—'}</span></div>
                    <div>Date: <span className="text-white">{txn.extracted_date || '—'}</span></div>
                    <div className="col-span-2">Payee: <span className="text-white">{txn.extracted_payee || '—'}</span></div>
                    <div className="col-span-2">Ref: <span className="text-white">{txn.extracted_reference || '—'}</span></div>
                    <div className="col-span-2">Guess: <span className="text-white">{txn.extracted_category_guess || '—'}</span></div>
                    {txn.extracted_raw_notes && <div className="col-span-2 text-slate-400 italic">“{txn.extracted_raw_notes}”</div>}
                  </div>
                </Card>
              )}
            </div>
          </div>
        )}
        <DialogFooter>
          {canAct && (
            <>
              <Button variant="ghost" onClick={() => setRejectOpen(true)} disabled={acting} className="text-rose-300 hover:text-rose-200 hover:bg-rose-500/10" data-testid="detail-reject-open">
                <XCircle className="h-4 w-4 mr-1.5" /> Reject
              </Button>
              <Button onClick={approve} disabled={acting} data-testid="detail-approve-btn" className="bg-emerald-500 hover:bg-emerald-400 text-[#0a1628] font-semibold">
                {acting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="h-4 w-4 mr-1.5" /> Approve</>}
              </Button>
            </>
          )}
          {canManage && txn && txn.status === 'Approved' && txn.reimbursement_needed && txn.reimbursement_status !== 'Paid' && (
            <Button onClick={markReimbursed} disabled={acting} data-testid="detail-mark-paid-btn" className="bg-amber-500 hover:bg-amber-400 text-[#0a1628] font-semibold">
              {acting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><HandCoins className="h-4 w-4 mr-1.5" /> Mark reimbursement paid</>}
            </Button>
          )}
          {isSubmitter && isPending && (
            <span className="text-xs text-slate-400 italic self-center mr-auto">
              Maker-checker: someone else with Finance-Reviewer permission needs to approve this.
            </span>
          )}
        </DialogFooter>
      </DialogContent>
      {/* Reject reason dialog */}
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent className="bg-[#0a1628] border-amber-500/20 text-slate-100 max-w-md">
          <DialogHeader><DialogTitle className="text-white">Reject transaction</DialogTitle></DialogHeader>
          <div className="space-y-2 py-2">
            <p className="text-xs text-slate-400">Tell the submitter what to correct so they can resubmit.</p>
            <Textarea rows={4} value={rejectNote} onChange={(e) => setRejectNote(e.target.value)} placeholder="e.g. Wrong amount — screenshot shows ₹14,500 not ₹12,450" className="bg-[#0f2240] border-amber-500/20 text-white" data-testid="reject-note-input" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRejectOpen(false)} className="text-slate-300 hover:text-white">Cancel</Button>
            <Button onClick={reject} disabled={acting || !rejectNote.trim()} data-testid="reject-confirm-btn" className="bg-rose-500 hover:bg-rose-400 text-[#0a1628] font-semibold">
              {acting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send back to submitter'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}

// --------------- Ledger + review table ---------------
function LedgerTable({ rows, onOpen }) {
  if (!rows || rows.length === 0) {
    return <div className="py-8 text-center text-slate-400 text-sm">No transactions match.</div>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="finance-table">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-amber-500/10">
            <th className="py-2 pr-3">Date</th>
            <th className="py-2 pr-3">Payee</th>
            <th className="py-2 pr-3">Category</th>
            <th className="py-2 pr-3 text-right">Amount</th>
            <th className="py-2 pr-3">Status</th>
            <th className="py-2 pr-3">Submitter</th>
            <th className="py-2 pr-3 w-8"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-amber-500/5">
          {rows.map((r) => (
            <tr key={r.id} className="hover:bg-amber-500/5 cursor-pointer" onClick={() => onOpen(r.id)} data-testid={`finance-row-${r.id}`}>
              <td className="py-2 pr-3 text-slate-300 whitespace-nowrap">{fmtDate(r.date)}</td>
              <td className="py-2 pr-3 text-white font-medium truncate max-w-[220px]">{r.payee}</td>
              <td className="py-2 pr-3"><Badge variant="outline" className={`${CATEGORY_STYLE[r.category]?.badge || ''} text-[11px]`}>{r.category}</Badge></td>
              <td className="py-2 pr-3 text-right text-white font-semibold whitespace-nowrap">{fmtINR(r.amount)}</td>
              <td className="py-2 pr-3"><Badge variant="outline" className={`${STATUS_STYLE[r.status] || ''} text-[11px]`}>{r.status}</Badge></td>
              <td className="py-2 pr-3 text-slate-400 truncate max-w-[160px]">{r.submitted_by_name}</td>
              <td className="py-2 pr-3 text-slate-500"><Eye className="h-4 w-4" /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// --------------- Page ---------------
export default function FinancePage() {
  const { user, hasPerm } = useAuth();
  // Permission-driven visibility. Owner + Admin auto-pass via hasPerm().
  const canReview = hasPerm('finance_reviewer');
  const canManage = hasPerm('finance_admin');

  const [tab, setTab] = useState('submit');
  const [meta, setMeta] = useState(null);
  const [rows, setRows] = useState([]);
  const [myRows, setMyRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [openTxnId, setOpenTxnId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    q: '', category: '', status: '', date_from: '', date_to: '', include_pending: true,
  });

  const loadAll = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.q) params.set('q', filters.q);
      if (filters.category) params.set('category', filters.category);
      if (filters.status) params.set('status', filters.status);
      if (filters.date_from) params.set('date_from', filters.date_from);
      if (filters.date_to) params.set('date_to', filters.date_to);

      const calls = [api.get('/finance/transactions/mine').catch(() => ({ data: [] }))];
      if (canReview) {
        calls.push(api.get(`/finance/transactions?${params}`).catch(() => ({ data: [] })));
        calls.push(api.get(`/finance/transactions/summary?include_pending=${filters.include_pending ? 'true' : 'false'}${filters.date_from ? `&date_from=${filters.date_from}` : ''}${filters.date_to ? `&date_to=${filters.date_to}` : ''}`).catch(() => ({ data: null })));
      }
      const [mineRes, txnRes, sumRes] = await Promise.all(calls);
      setMyRows(mineRes.data || []);
      if (canReview) {
        setRows(txnRes?.data || []);
        setSummary(sumRes?.data || null);
      }
    } catch (_e) {
      setRows([]); setMyRows([]); setSummary(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canReview) api.get('/finance/meta').then((r) => setMeta(r.data)).catch(() => {});
    else setMeta({ categories: ['Vendor Payment', 'Employee Salary', 'Advance', 'Expense', 'Other'], statuses: ['Pending Review', 'Approved', 'Rejected'], employees: [] });
  }, [canReview]);
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [canReview, filters.q, filters.category, filters.status, filters.date_from, filters.date_to, filters.include_pending]);

  const pendingRows = useMemo(() => rows.filter((r) => r.status === 'Pending Review'), [rows]);
  const reimbursementRows = useMemo(
    () => rows.filter((r) => r.reimbursement_needed && r.status === 'Approved' && r.reimbursement_status !== 'Paid'),
    [rows],
  );

  const exportExcel = async () => {
    try {
      const params = new URLSearchParams();
      params.set('include_pending', filters.include_pending ? 'true' : 'false');
      if (filters.date_from) params.set('date_from', filters.date_from);
      if (filters.date_to) params.set('date_to', filters.date_to);
      const resp = await api.get(`/finance/transactions/export?${params}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `shine_finance_ledger_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Export downloaded');
    } catch (e) {
      toast.error('Export failed');
    }
  };

  return (
    <div>
      <PageHeader
        icon={Wallet}
        title="Finance Ledger"
        subtitle="AI-assisted transaction entries. Submitter uploads a screenshot, another reviewer approves — no more manual Excel."
        actions={canReview ? (
          <Button onClick={exportExcel} data-testid="finance-export-btn" className="bg-emerald-500 hover:bg-emerald-400 text-[#0a1628] font-semibold">
            <FileSpreadsheet className="h-4 w-4 mr-1.5" /> Export to Excel
          </Button>
        ) : null}
      />

      {/* Summary cards — reviewer-only */}
      {canReview && summary && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6" data-testid="finance-summary">
          {summary.by_category.map((s) => {
            const Icon = CATEGORY_STYLE[s.category]?.icon || FileSpreadsheet;
            return (
              <Card key={s.category} className="shine-card p-3 rounded-xl">
                <div className="flex items-center gap-2">
                  <div className="h-8 w-8 rounded-lg bg-amber-500/10 border border-amber-500/25 flex items-center justify-center">
                    <Icon className="h-4 w-4 text-amber-300" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400 truncate">{s.category}</div>
                    <div className="text-sm font-extrabold text-white truncate">{fmtINR(s.total)}</div>
                    <div className="text-[10px] text-slate-500">{s.count} entries</div>
                  </div>
                </div>
              </Card>
            );
          })}
          <Card className="shine-card p-3 rounded-xl border-amber-500/40">
            <div className="text-[10px] uppercase tracking-wider text-amber-300">Grand total</div>
            <div className="text-sm font-extrabold text-white truncate">{fmtINR(summary.grand_total)}</div>
            <div className="text-[10px] text-slate-400">{summary.grand_count} entries · {summary.include_pending ? 'incl. pending' : 'approved only'}</div>
          </Card>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab} data-testid="finance-tabs">
        <TabsList className="bg-[#0a1628] border border-amber-500/20 flex-wrap h-auto">
          <TabsTrigger value="submit" data-testid="tab-submit" className="data-[state=active]:bg-amber-500/15 data-[state=active]:text-amber-200 text-slate-300">
            <Upload className="h-4 w-4 mr-1.5" /> Submit
          </TabsTrigger>
          <TabsTrigger value="mine" data-testid="tab-mine" className="data-[state=active]:bg-amber-500/15 data-[state=active]:text-amber-200 text-slate-300">
            <ReceiptText className="h-4 w-4 mr-1.5" /> My Claims ({myRows.length})
          </TabsTrigger>
          {canReview && (
            <TabsTrigger value="review" data-testid="tab-review" className="data-[state=active]:bg-amber-500/15 data-[state=active]:text-amber-200 text-slate-300">
              <CheckCircle2 className="h-4 w-4 mr-1.5" /> Review Queue ({pendingRows.length})
            </TabsTrigger>
          )}
          {canReview && (
            <TabsTrigger value="ledger" data-testid="tab-ledger" className="data-[state=active]:bg-amber-500/15 data-[state=active]:text-amber-200 text-slate-300">
              <FileSpreadsheet className="h-4 w-4 mr-1.5" /> Ledger
            </TabsTrigger>
          )}
          {canManage && (
            <TabsTrigger value="reimburse" data-testid="tab-reimburse" className="data-[state=active]:bg-amber-500/15 data-[state=active]:text-amber-200 text-slate-300">
              <HandCoins className="h-4 w-4 mr-1.5" /> Reimbursements ({reimbursementRows.length})
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="submit" className="mt-4">
          <SubmitForm meta={meta} onCreated={() => { loadAll(); setTab('mine'); }} />
        </TabsContent>

        <TabsContent value="mine" className="mt-4">
          <Card className="shine-card p-5 rounded-xl">
            <h3 className="text-white font-bold mb-3 flex items-center gap-2"><ReceiptText className="h-4 w-4 text-amber-300" /> My submitted claims</h3>
            {loading ? <div className="py-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-amber-300" /></div>
              : <LedgerTable rows={myRows} onOpen={setOpenTxnId} />}
          </Card>
        </TabsContent>

        {canReview && (
          <TabsContent value="review" className="mt-4">
            <Card className="shine-card p-5 rounded-xl">
              <h3 className="text-white font-bold mb-3 flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-amber-300" /> Pending review</h3>
              {loading ? <div className="py-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-amber-300" /></div>
                : <LedgerTable rows={pendingRows} onOpen={setOpenTxnId} />}
            </Card>
          </TabsContent>
        )}

        {canManage && (
          <TabsContent value="reimburse" className="mt-4">
            <Card className="shine-card p-5 rounded-xl">
              <h3 className="text-white font-bold mb-3 flex items-center gap-2"><HandCoins className="h-4 w-4 text-amber-300" /> Approved claims waiting to be paid back</h3>
              <p className="text-xs text-slate-400 mb-3">Team members who paid out-of-pocket. Open a row to mark reimbursement paid — this is real money leaving the company account, so only Finance Admin can do it.</p>
              {loading ? <div className="py-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-amber-300" /></div>
                : <LedgerTable rows={reimbursementRows} onOpen={setOpenTxnId} />}
            </Card>
          </TabsContent>
        )}

        {canReview && (
        <TabsContent value="ledger" className="mt-4 space-y-3">
          <Card className="shine-card p-3 rounded-xl">
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative flex-1 min-w-[220px]">
                <Search className="h-3.5 w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-500" />
                <Input value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} placeholder="Search payee, vendor, employee, note, ref…" className="pl-7 bg-[#0f2240] border-amber-500/20 text-white h-9" data-testid="finance-search-input" />
              </div>
              <Select value={filters.category || '_all'} onValueChange={(v) => setFilters({ ...filters, category: v === '_all' ? '' : v })}>
                <SelectTrigger className="w-40 h-9 bg-[#0f2240] border-amber-500/20" data-testid="finance-filter-category"><SelectValue placeholder="All categories" /></SelectTrigger>
                <SelectContent className="bg-[#0a1628] border-amber-500/20">
                  <SelectItem value="_all">All categories</SelectItem>
                  {(meta?.categories || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filters.status || '_all'} onValueChange={(v) => setFilters({ ...filters, status: v === '_all' ? '' : v })}>
                <SelectTrigger className="w-40 h-9 bg-[#0f2240] border-amber-500/20" data-testid="finance-filter-status"><SelectValue placeholder="All statuses" /></SelectTrigger>
                <SelectContent className="bg-[#0a1628] border-amber-500/20">
                  <SelectItem value="_all">All statuses</SelectItem>
                  {(meta?.statuses || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} className="w-40 h-9 bg-[#0f2240] border-amber-500/20 text-white" title="From date" />
              <Input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} className="w-40 h-9 bg-[#0f2240] border-amber-500/20 text-white" title="To date" />
              <label className="flex items-center gap-1 text-xs text-slate-400 cursor-pointer">
                <input type="checkbox" checked={filters.include_pending} onChange={(e) => setFilters({ ...filters, include_pending: e.target.checked })} data-testid="finance-include-pending" />
                Include pending
              </label>
            </div>
          </Card>
          <Card className="shine-card p-5 rounded-xl">
            {loading ? <div className="py-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-amber-300" /></div>
              : <LedgerTable rows={rows} onOpen={setOpenTxnId} />}
          </Card>
        </TabsContent>
        )}

      </Tabs>

      {openTxnId && (
        <TransactionDetail
          txnId={openTxnId}
          currentUserId={user?.id}
          canReview={canReview}
          canManage={canManage}
          onClose={() => setOpenTxnId(null)}
          onChanged={loadAll}
        />
      )}
    </div>
  );
}
